import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Star, Gift, Trophy, Users, TrendingUp, Settings,
  Plus, Edit2, Trash2, Crown, Award, BarChart3,
  Sparkles, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoyaltyManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [settings, setSettings] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [members, setMembers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [rewardForm, setRewardForm] = useState({
    name: '',
    description: '',
    points_required: 100,
    reward_type: 'discount',
    reward_value: 10,
    max_per_user: 1,
    tier_required: '',
    is_active: true
  });

  const [awardForm, setAwardForm] = useState({
    action: 'manual_award',
    points: 50,
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [settingsRes, rewardsRes, membersRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/loyalty/admin/settings`, { headers }),
        axios.get(`${API_URL}/api/loyalty/admin/rewards`, { headers }),
        axios.get(`${API_URL}/api/loyalty/admin/members`, { headers }),
        axios.get(`${API_URL}/api/loyalty/admin/analytics`, { headers })
      ]);

      setSettings(settingsRes.data);
      setRewards(rewardsRes.data.rewards || []);
      setMembers(membersRes.data.members || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error loading loyalty data:', error);
      toast.error('Failed to load loyalty program data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProgram = async (enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/loyalty/admin/settings`,
        { ...settings, is_enabled: enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings({ ...settings, is_enabled: enabled });
      toast.success(`Loyalty program ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleSaveReward = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingReward) {
        await axios.put(`${API_URL}/api/loyalty/admin/rewards/${editingReward.id}`, rewardForm, { headers });
        toast.success('Reward updated');
      } else {
        await axios.post(`${API_URL}/api/loyalty/admin/rewards`, rewardForm, { headers });
        toast.success('Reward created');
      }

      setShowRewardModal(false);
      resetRewardForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save reward');
    }
  };

  const handleDeleteReward = async (rewardId) => {
    if (!window.confirm('Are you sure you want to deactivate this reward?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/loyalty/admin/rewards/${rewardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reward deactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to delete reward');
    }
  };

  const handleAwardPoints = async () => {
    if (!selectedMember) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/loyalty/admin/award-points`,
        {
          user_id: selectedMember.user_id,
          action: awardForm.action,
          points: awardForm.points,
          description: awardForm.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Awarded ${awardForm.points} points to ${selectedMember.full_name}`);
      setShowAwardModal(false);
      setSelectedMember(null);
      loadData();
    } catch (error) {
      toast.error('Failed to award points');
    }
  };

  const openEditReward = (reward) => {
    setEditingReward(reward);
    setRewardForm({
      name: reward.name,
      description: reward.description,
      points_required: reward.points_required,
      reward_type: reward.reward_type,
      reward_value: reward.reward_value,
      max_per_user: reward.max_per_user || 1,
      tier_required: reward.tier_required || '',
      is_active: reward.is_active
    });
    setShowRewardModal(true);
  };

  const resetRewardForm = () => {
    setEditingReward(null);
    setRewardForm({
      name: '',
      description: '',
      points_required: 100,
      reward_type: 'discount',
      reward_value: 10,
      max_per_user: 1,
      tier_required: '',
      is_active: true
    });
  };

  const filteredMembers = members.filter(m => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (m.full_name || '').toLowerCase().includes(query) ||
           (m.email || '').toLowerCase().includes(query);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="loyalty-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Crown className="h-7 w-7 text-amber-500" />
            Loyalty Program
          </h1>
          <p className="text-gray-600 mt-1">
            Reward your members for their engagement
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Program Status:</span>
            <Switch
              checked={settings?.is_enabled}
              onCheckedChange={handleToggleProgram}
            />
            <Badge variant={settings?.is_enabled ? 'default' : 'secondary'}>
              {settings?.is_enabled ? 'Active' : 'Disabled'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Members</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.total_members}</p>
                </div>
                <Users className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Points Issued</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.total_points_issued?.toLocaleString()}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Points Redeemed</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.total_points_redeemed?.toLocaleString()}
                  </p>
                </div>
                <Gift className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Redemptions (30d)</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.redemptions_this_month}</p>
                </div>
                <Award className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tier Distribution */}
      {analytics?.tier_distribution && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Tier Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              {Object.entries(analytics.tier_distribution).map(([tier, count]) => (
                <div key={tier} className="flex-1 text-center p-3 bg-gray-50 rounded-lg">
                  <div className={`inline-flex items-center justify-center h-10 w-10 rounded-full mb-2 ${
                    tier === 'Platinum' ? 'bg-gray-200' :
                    tier === 'Gold' ? 'bg-yellow-100' :
                    tier === 'Silver' ? 'bg-gray-100' :
                    'bg-amber-100'
                  }`}>
                    {tier === 'Platinum' ? <Crown className="h-5 w-5 text-gray-600" /> :
                     tier === 'Gold' ? <Trophy className="h-5 w-5 text-yellow-600" /> :
                     <Star className="h-5 w-5 text-amber-600" />}
                  </div>
                  <p className="font-semibold">{tier}</p>
                  <p className="text-2xl font-bold text-purple-600">{count}</p>
                  <p className="text-xs text-gray-500">members</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="rewards">
            <Gift className="h-4 w-4 mr-2" />
            Rewards
          </TabsTrigger>
          <TabsTrigger value="members">
            <Users className="h-4 w-4 mr-2" />
            Members
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Point Values */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Point Values</CardTitle>
              <CardDescription>How members earn points</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(settings?.point_values || {}).map(([action, points]) => (
                  <div key={action} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 capitalize">{action.replace(/_/g, ' ')}</p>
                    <p className="text-xl font-bold text-purple-600">+{points}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rewards" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Rewards Catalog</h3>
            <Button onClick={() => { resetRewardForm(); setShowRewardModal(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Reward
            </Button>
          </div>

          {rewards.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Gift className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-600">No rewards created yet</p>
                <Button className="mt-4" onClick={() => setShowRewardModal(true)}>
                  Create First Reward
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rewards.map((reward) => (
                <Card key={reward.id} className={!reward.is_active ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{reward.name}</CardTitle>
                        <CardDescription>{reward.description}</CardDescription>
                      </div>
                      <Badge variant={reward.is_active ? 'default' : 'secondary'}>
                        {reward.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Points Required</span>
                        <span className="font-bold text-purple-600">{reward.points_required}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type</span>
                        <span className="capitalize">{reward.reward_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Redemptions</span>
                        <span>{reward.total_redemptions || 0}</span>
                      </div>
                      {reward.tier_required && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tier Required</span>
                          <Badge variant="outline">{reward.tier_required}+</Badge>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => openEditReward(reward)}>
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteReward(reward.id)}>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="members" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-4 font-medium">Member</th>
                    <th className="text-left p-4 font-medium">Tier</th>
                    <th className="text-right p-4 font-medium">Available</th>
                    <th className="text-right p-4 font-medium">Lifetime</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((member) => (
                    <tr key={member.user_id} className="border-t">
                      <td className="p-4">
                        <p className="font-medium">{member.full_name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{member.email}</p>
                      </td>
                      <td className="p-4">
                        <Badge
                          style={{ backgroundColor: member.current_tier?.color }}
                          className="text-white"
                        >
                          {member.current_tier?.name}
                        </Badge>
                      </td>
                      <td className="p-4 text-right font-mono">
                        {member.available_points?.toLocaleString()}
                      </td>
                      <td className="p-4 text-right font-mono">
                        {member.lifetime_points?.toLocaleString()}
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedMember(member);
                            setShowAwardModal(true);
                          }}
                        >
                          <Sparkles className="h-4 w-4 mr-1" />
                          Award
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Membership Tiers</CardTitle>
              <CardDescription>Configure point thresholds for each tier</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(settings?.tiers || []).map((tier, index) => (
                  <div key={tier.name} className="flex items-center gap-4 p-4 border rounded-lg">
                    <div
                      className="h-10 w-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: tier.color + '33' }}
                    >
                      <Star className="h-5 w-5" style={{ color: tier.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{tier.name}</p>
                      <p className="text-sm text-gray-500">
                        {tier.min_points.toLocaleString()}+ points
                      </p>
                    </div>
                    <div className="text-sm text-gray-600">
                      {tier.benefits?.length || 0} benefits
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reward Modal */}
      <Dialog open={showRewardModal} onOpenChange={setShowRewardModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingReward ? 'Edit Reward' : 'Create Reward'}</DialogTitle>
            <DialogDescription>
              {editingReward ? 'Update the reward details' : 'Create a new reward for members to redeem'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reward Name</Label>
              <Input
                value={rewardForm.name}
                onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })}
                placeholder="e.g., 10% Off Next Visit"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={rewardForm.description}
                onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })}
                placeholder="Describe the reward..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Points Required</Label>
                <Input
                  type="number"
                  min="1"
                  value={rewardForm.points_required}
                  onChange={(e) => setRewardForm({ ...rewardForm, points_required: parseInt(e.target.value) })}
                />
              </div>

              <div className="space-y-2">
                <Label>Reward Type</Label>
                <Select
                  value={rewardForm.reward_type}
                  onValueChange={(v) => setRewardForm({ ...rewardForm, reward_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount">Percentage Discount</SelectItem>
                    <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                    <SelectItem value="free_service">Free Service</SelectItem>
                    <SelectItem value="upgrade">Plan Upgrade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reward Value</Label>
                <Input
                  type="number"
                  min="0"
                  value={rewardForm.reward_value}
                  onChange={(e) => setRewardForm({ ...rewardForm, reward_value: parseFloat(e.target.value) })}
                  placeholder={rewardForm.reward_type === 'discount' ? '% off' : '$ value'}
                />
              </div>

              <div className="space-y-2">
                <Label>Tier Required</Label>
                <Select
                  value={rewardForm.tier_required || 'all'}
                  onValueChange={(v) => setRewardForm({ ...rewardForm, tier_required: v === 'all' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All tiers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="Silver">Silver+</SelectItem>
                    <SelectItem value="Gold">Gold+</SelectItem>
                    <SelectItem value="Platinum">Platinum Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={rewardForm.is_active}
                onCheckedChange={(v) => setRewardForm({ ...rewardForm, is_active: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRewardModal(false)}>Cancel</Button>
            <Button onClick={handleSaveReward}>
              {editingReward ? 'Update' : 'Create'} Reward
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Award Points Modal */}
      <Dialog open={showAwardModal} onOpenChange={setShowAwardModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Award Points</DialogTitle>
            <DialogDescription>
              Award points to {selectedMember?.full_name || 'member'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Action Type</Label>
              <Select
                value={awardForm.action}
                onValueChange={(v) => setAwardForm({ ...awardForm, action: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual_award">Manual Award</SelectItem>
                  <SelectItem value="benefit_usage">Benefit Usage</SelectItem>
                  <SelectItem value="birthday_bonus">Birthday Bonus</SelectItem>
                  <SelectItem value="referral_completed">Referral Bonus</SelectItem>
                  <SelectItem value="loyalty_bonus">Loyalty Bonus</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Points to Award</Label>
              <Input
                type="number"
                min="1"
                value={awardForm.points}
                onChange={(e) => setAwardForm({ ...awardForm, points: parseInt(e.target.value) })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={awardForm.description}
                onChange={(e) => setAwardForm({ ...awardForm, description: e.target.value })}
                placeholder="Reason for awarding points..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAwardModal(false)}>Cancel</Button>
            <Button onClick={handleAwardPoints}>
              <Sparkles className="h-4 w-4 mr-2" />
              Award {awardForm.points} Points
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoyaltyManagement;
